"use client";
export const dynamic = "force-dynamic";

import Link from "next/link";
import { motion } from "framer-motion";
import {
  Bot,
  Brain,
  BookOpen,
  Sparkles,
  MessageSquare,
  ShieldCheck,
  Languages,
  Globe,
  Zap,
  GitBranch,
  FlaskConical,
  BarChart3,
  Lock,
  ArrowLeft,
  PlayCircle,
  Wand2,
  Cpu,
  GraduationCap,
  Clock,
} from "lucide-react";
import SiteHeader from "@/components/landing/SiteHeader";
import SiteFooter from "@/components/landing/SiteFooter";
import {
  SolutionShell,
  Orbs,
  GridBg,
  Section,
  SectionHeader,
  GiantStats,
  LineFeatures,
  SplitFeature,
  StepsTimeline,
  CompareSplit,
  BigQuote,
  MiniTestimonials,
  FAQ,
  FinalCTA,
  HeroBadge,
  HeroTitle,
  PricingStrip,
} from "@/components/landing/solutions/SolutionSections";

function Page() {
  return (
    <SolutionShell>
      <SiteHeader hashMode={false} />

      <section className="relative min-h-screen flex items-center pt-32 pb-20 overflow-hidden">
        <GridBg />
        <Orbs className="inset-0" />
        <div className="container mx-auto px-6 max-w-7xl relative grid lg:grid-cols-[1.1fr_1fr] gap-14 items-center">
          <div className="text-center lg:text-right">
            <HeroBadge>AI Native · با شخصیت برند شما</HeroBadge>
            <HeroTitle pre="دستیار هوش مصنوعی" gradient="با لحن برند شما" />
            <p className="text-base md:text-lg text-muted-foreground max-w-xl mx-auto lg:mx-0 mb-10 leading-relaxed">
              ترندی فقط یک چت‌بات نیست؛ روی دانش، محصول و لحن برند شما آموزش می‌بیند و در همه‌ی
              کانال‌ها حاضر است.
            </p>
            <div className="flex flex-col sm:flex-row items-center lg:items-start justify-center lg:justify-start gap-4 mb-10">
              <Link
                href="/auth"
                className="group w-full sm:w-auto px-8 py-4 rounded-full bg-foreground text-background font-bold flex items-center justify-center gap-2 shadow-2xl shadow-primary-main/20"
              >
                ساخت دستیار <ArrowLeft size={18} />
              </Link>
              <Link
                href="/trendi"
                className="group flex items-center gap-3 text-foreground/75 hover:text-foreground"
              >
                <span className="w-12 h-12 rounded-full bg-foreground/5 border border-border grid place-items-center">
                  <PlayCircle size={18} />
                </span>
                <span className="text-sm font-medium">ترندی رو ببین</span>
              </Link>
            </div>
            <div className="flex flex-wrap items-center justify-center lg:justify-start gap-x-7 gap-y-3 text-xs text-muted-foreground/80">
              <span className="flex items-center gap-1.5">
                <ShieldCheck size={13} className="text-primary-main" /> داده‌ها در ایران
              </span>
              <span className="flex items-center gap-1.5">
                <Clock size={13} className="text-secondary-main" /> پاسخ &lt;۱.۲s
              </span>
            </div>
          </div>
          <PersonaMock />
        </div>
      </section>

      <GiantStats
        items={[
          { value: "%۸۶", label: "پاسخ بدون اپراتور" },
          { value: "<۱.۲s", label: "زمان پاسخ" },
          { value: "+۲۰", label: "زبان پشتیبانی" },
          { value: "۲۴/۷", label: "بدون استراحت" },
        ]}
      />

      <Section>
        <SectionHeader eyebrow="قابلیت‌ها" title="هوش مصنوعی، اختصاصی شما" />
        <LineFeatures
          items={[
            {
              icon: Brain,
              title: "درک عمیق فارسی محاوره",
              desc: "محاوره، دیالکت و اصطلاحات کاری — مثل یک اپراتور انسانی.",
            },
            {
              icon: BookOpen,
              title: "Knowledge Base قدرتمند",
              desc: "PDF، URL، FAQ و فایل اکسل — همه قابل آموزش.",
            },
            {
              icon: Wand2,
              title: "شخصیت برند قابل تنظیم",
              desc: "لحن، ایموجی، طول جمله، نام persona.",
            },
            {
              icon: GitBranch,
              title: "Hand-off هوشمند",
              desc: "وقتی AI جواب نداشت، به اپراتور انسانی پاس می‌دهد.",
            },
            {
              icon: ShieldCheck,
              title: "Safety Layer",
              desc: "از پاسخ غلط درباره قیمت، حقوقی یا داده‌های حساس جلوگیری.",
            },
            { icon: Languages, title: "چندزبانه", desc: "فارسی، انگلیسی، عربی، ترکی و ۲۰+ زبان." },
            { icon: FlaskConical, title: "Playground و A/B", desc: "قبل از انتشار تست بگیر." },
            {
              icon: BarChart3,
              title: "Analytics و شکاف دانش",
              desc: "موضوعات داغ و سؤالاتی که AI نمی‌دونست.",
            },
          ]}
        />
      </Section>

      <Section className="border-t border-border">
        <SplitFeature
          eyebrow="Knowledge Base"
          title={
            <>
              یک‌بار آموزش بده، <span className="brand-gradient-text">همیشه</span> پاسخ بگیر
            </>
          }
          desc="فایل، لینک و FAQ رو آپلود کن. ترندی همه چیز رو می‌فهمه و با منبع پاسخ می‌ده."
          bullets={[
            "پشتیبانی PDF, DOCX, URL, CSV",
            "نقل‌قول از منبع در پاسخ",
            "به‌روزرسانی خودکار از سایت",
          ]}
          slot={<MockKnowledge />}
        />
      </Section>

      <Section className="border-t border-border">
        <SplitFeature
          reverse
          eyebrow="Persona سفارشی"
          title={
            <>
              هر workspace، <span className="brand-gradient-text">یک ترندی</span> اختصاصی
            </>
          }
          desc="نام، آواتار، رنگ و لحن — همه قابل تغییر. می‌تونی اسمش رو بذاری «آوا» یا «مایا»."
          bullets={["نام و آواتار اختصاصی", "لحن: رسمی / دوستانه / فان", "رنگ persona خودش"]}
          slot={<MockPersona />}
        />
      </Section>

      <Section className="border-t border-border">
        <SectionHeader eyebrow="چطور بسازم" title="در ۴ گام، دستیار اختصاصی" />
        <StepsTimeline
          steps={[
            { title: "تعریف شخصیت", desc: "نام، لحن، رنگ، آواتار." },
            { title: "آموزش با دانش", desc: "محصولات، FAQ، سیاست‌ها." },
            { title: "اتصال کانال", desc: "وب، اینستا، تلگرام و …" },
            { title: "پایش و یادگیری", desc: "هر هفته شکاف‌ها رو پر کن." },
          ]}
        />
      </Section>

      <Section className="border-t border-border">
        <SectionHeader eyebrow="مقایسه" title="ترندی در برابر چت‌بات‌های ساده" accent="secondary" />
        <CompareSplit
          ours={[
            "درک محاوره فارسی",
            "آموزش روی دانش شما",
            "Hand-off هوشمند به انسان",
            "حافظه مکالمه",
            "نرخ پاسخ موفق ۸۶٪",
          ]}
          theirs={[
            "پاسخ بر اساس کلمه کلیدی",
            "اسکریپت ثابت",
            "بن‌بست در سؤالات سخت",
            "هر بار از صفر",
            "نرخ موفق ~۲۰٪",
          ]}
        />
      </Section>

      <Section>
        <BigQuote
          quote="ترندی پاسخ‌های مشاوره‌ای ما رو با همون لحن می‌ده. تیکت‌های ساده ۸۰٪ کاهش پیدا کرد."
          name="آیدا نوری"
          role="آموزشگاه راهبر"
        />
      </Section>

      <Section className="border-t border-border">
        <SectionHeader eyebrow="مشتریان" title="آن‌ها به ترندی اعتماد کردند" />
        <MiniTestimonials
          items={[
            {
              name: "رضا تیموری",
              role: "بیمه پاسارگاد",
              quote: "تیکت‌های ساده ۸۰٪ کاهش پیدا کرد.",
            },
            { name: "Mahdis K.", role: "Cosmetics", quote: "Multilingual response is flawless." },
            { name: "نگار شفیعی", role: "املاک پارس", quote: "ترندی شب‌ها هم مشتری جذب می‌کنه." },
          ]}
        />
      </Section>

      <Section>
        <SectionHeader eyebrow="پرسش‌ها" title="سؤالات متداول" />
        <FAQ
          items={[
            { q: "داده‌ها کجا ذخیره می‌شه؟", a: "روی سرورهای ایران، رمزنگاری end-to-end." },
            {
              q: "از چه مدلی استفاده می‌کنه؟",
              a: "ترکیبی از مدل‌های open-source و اختصاصی fine-tune شده.",
            },
            { q: "تعداد پاسخ محدوده؟", a: "بسته به پلن، اما همه‌ی پلن‌ها سقف مشخص و شفاف دارند." },
          ]}
        />
      </Section>

      <PricingStrip />
      <FinalCTA
        title={
          <>
            دستیار <span className="brand-gradient-text">اختصاصی</span> برند خودت بساز
          </>
        }
        desc="آموزش با دانش شما، در چند دقیقه."
      />
      <SiteFooter />
    </SolutionShell>
  );
}

function PersonaMock() {
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      className="relative rounded-3xl border border-border bg-background p-6 shadow-2xl"
    >
      <div className="absolute -inset-6 rounded-[3rem] bg-gradient-to-br from-primary-main/30 to-secondary-main/30 blur-3xl opacity-50 -z-10" />
      <div className="flex items-center gap-3 pb-4 border-b border-border">
        <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-primary-main to-secondary-main flex items-center justify-center shadow-lg">
          <Sparkles size={20} className="text-foreground" />
        </div>
        <div>
          <div className="font-black text-foreground">ترندی · Trendi</div>
          <div className="text-[11px] text-muted-foreground flex items-center gap-1.5">
            <span className="w-1.5 h-1.5 rounded-full bg-primary-main animate-pulse" /> در حال تایپ…
          </div>
        </div>
        <div className="ms-auto px-2 py-1 rounded-md bg-secondary-main/15 text-secondary-main text-[10px] font-black">
          v۳.۲
        </div>
      </div>
      <div className="space-y-3 py-5">
        <div className="bg-foreground/[0.08] border border-border text-foreground text-sm px-3 py-2 rounded-xl rounded-tr-sm max-w-[80%] mr-auto">
          سلام، گارانتی محصول چقدره؟
        </div>
        <div className="bg-primary-main/15 border border-primary-main/30 text-foreground text-sm px-3 py-2 rounded-xl rounded-tl-sm max-w-[85%]">
          گارانتی همه محصولات ما <b>۱۸ ماه</b>ه ✨ و تعویض رایگان داره. می‌خوای راهنماش رو بفرستم؟
        </div>
        <div className="flex gap-2 pt-1">
          <button className="px-3 py-1.5 rounded-lg bg-foreground/[0.08] border border-border text-foreground text-xs font-bold">
            بله، بفرست
          </button>
          <button className="px-3 py-1.5 rounded-lg bg-foreground/[0.08] border border-border text-foreground text-xs font-bold">
            مرجوع کالا
          </button>
        </div>
      </div>
      <div className="grid grid-cols-3 gap-2 pt-4 border-t border-border">
        <Stat icon={Cpu} v="۸۶٪" l="پاسخ AI" />
        <Stat icon={Zap} v="۱.۲s" l="سرعت" />
        <Stat icon={MessageSquare} v="۲.۴k" l="گفتگو/روز" />
      </div>
    </motion.div>
  );
}
function Stat({ icon: I, v, l }: any) {
  return (
    <div className="rounded-xl bg-foreground/[0.04] border border-border/60 p-2.5 text-center">
      <I size={14} className="text-primary-main mx-auto" />
      <div className="text-sm font-black text-foreground mt-1">{v}</div>
      <div className="text-[9px] text-muted-foreground">{l}</div>
    </div>
  );
}

function MockKnowledge() {
  return (
    <div className="rounded-3xl border border-border bg-foreground/[0.04] p-6">
      <div className="flex items-center gap-2 text-sm font-black text-foreground mb-4">
        <BookOpen size={16} className="text-primary-main" /> Knowledge Sources
      </div>
      {[
        { n: "catalog-1404.pdf", t: "PDF · ۲۴۰ ص", s: "آموزش‌دیده" },
        { n: "trendi.shop/faq", t: "URL · auto-sync", s: "آموزش‌دیده" },
        { n: "policies.docx", t: "DOCX · ۱۲ ص", s: "در حال آموزش" },
        { n: "products.csv", t: "CSV · ۸۲۰ ردیف", s: "آموزش‌دیده" },
      ].map((s) => (
        <div
          key={s.n}
          className="flex items-center justify-between py-2.5 border-b border-border/60 last:border-0"
        >
          <div>
            <div className="text-sm font-bold text-foreground">{s.n}</div>
            <div className="text-[10px] text-muted-foreground/80">{s.t}</div>
          </div>
          <span
            className={`text-[10px] font-black px-2 py-1 rounded-md ${s.s === "آموزش‌دیده" ? "bg-primary-main/15 text-primary-main" : "bg-secondary-main/15 text-secondary-main"}`}
          >
            {s.s}
          </span>
        </div>
      ))}
    </div>
  );
}

function MockPersona() {
  return (
    <div className="rounded-3xl border border-border bg-foreground/[0.04] p-6 space-y-4">
      <div className="flex items-center gap-2 text-sm font-black text-foreground">
        <GraduationCap size={16} className="text-secondary-main" /> Persona Designer
      </div>
      {[
        { l: "نام", v: "آوا" },
        { l: "لحن", v: "دوستانه + ایموجی" },
        { l: "رنگ", v: "Teal × Orange" },
        { l: "زبان", v: "FA / EN" },
      ].map((p) => (
        <div
          key={p.l}
          className="flex items-center justify-between py-2 border-b border-border/60 last:border-0"
        >
          <span className="text-xs text-muted-foreground">{p.l}</span>
          <span className="text-sm font-bold text-foreground">{p.v}</span>
        </div>
      ))}
      <div className="pt-3 border-t border-border flex items-center gap-2">
        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-primary-main to-secondary-main grid place-items-center">
          <Sparkles size={16} className="text-foreground" />
        </div>
        <div className="text-xs text-foreground">«سلام عزیز 🌸 من آوا هستم، چطور کمکت کنم؟»</div>
      </div>
    </div>
  );
}

export default Page;
